import { useCallback, type FC } from "react";
import { WorkflowList } from "@/components/automations/AutomationList";
import { AutomationsEducationPage } from "@/routes/automations/AutomationsEducationPage";
import IconLock from "central-icons/IconLock";
import { AutomationsPageHeader } from "@/routes/automations/AutomationsPageHeader";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { useFlagWorkflowsEnabled, WorkflowAccessLevel } from "@/hooks/use-flag";
import { NewAutomationButton } from "@/components/automations/NewAutomationButton";
import { useNavigate } from "react-router-dom";
import type { AutomationTemplate } from "@/utils/automation-templates";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { useOrganization } from "@/queries/organization-queries";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { PermissionDenied } from "@/components/flexkit/PermissionDenied";
import { AnimatedLoadingContainer } from "@/components/ona/AnimatedLoadingContainer";
import { newWorkflowUrl } from "@/utils/automations";

export const AutomationsPage: FC = () => {
    const { accessLevel: workflowAccessLevel, loading: isLoadingFlag } = useFlagWorkflowsEnabled();

    if (isLoadingFlag) {
        return <LoadingState />;
    }

    switch (workflowAccessLevel) {
        case WorkflowAccessLevel.Full:
            return <AutomationsPageContent />;
        case WorkflowAccessLevel.Teaser:
            return <AutomationsEducationPage />;
        case WorkflowAccessLevel.None:
        default:
            return null;
    }
};

const AutomationsPageContent: FC = () => {
    const { data: organization } = useOrganization();
    const { hasPermission, isLoading: isLoadingPermissions } = useResourcePermissions();

    const canCreateWorkflows =
        organization && hasPermission(ResourceType.ORGANIZATION, organization.id, "workflow:create");

    const navigate = useNavigate();

    const handleTemplateSelect = useCallback(
        (template: AutomationTemplate) => {
            navigate(newWorkflowUrl({ templateId: template.id }));
        },
        [navigate],
    );

    const actions = canCreateWorkflows ? (
        <NewAutomationButton
            onTemplateSelect={handleTemplateSelect}
            testId="new-automation-dialog-workflows-page"
            trackingId="show-new-automation-dialog-automations-page"
        />
    ) : undefined;

    const renderContent = () => {
        if (canCreateWorkflows) {
            return <WorkflowList onTemplateSelect={handleTemplateSelect} />;
        }
        return (
            <PermissionDenied
                icon={<IconLock size={24} className="text-content-base" />}
                title="You don't have access to Automations"
                description={
                    <>
                        Currently you don&apos;t have permissions to view or manage automations.
                        <br />
                        Please contact your <span className="font-medium">Organization Admin</span> to request
                        access.
                    </>
                }
                data-testid="automations-permission-denied-page"
            />
        );
    };

    return (
        <div data-testid="workflows-page" className="flex h-full grow flex-col gap-6">
            <AutomationsPageHeader actions={isLoadingPermissions ? undefined : actions} />
            <AnimatedLoadingContainer isLoading={isLoadingPermissions} message="Preparing automations">
                {renderContent()}
            </AnimatedLoadingContainer>
        </div>
    );
};
